import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { LockKeyholeIcon, RefreshCwIcon } from "lucide-react";
import { useState } from "react";

import type { AppDetailResponse } from "../../shared/schemas";
import { appQuery } from "../api/queries";
import { useFrameLifecycle } from "../desktop/use-frame-lifecycle";
import { AuthenticatedShell } from "../components/AuthenticatedShell";
import { DeploymentUpdateNotice } from "../components/DeploymentUpdates";
import { useDeploymentUpdates } from "../components/deployment-update-context";
import { OfflineState, useOnlineStatus } from "../components/OfflineState";
import { buttonVariants, Button } from "../components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "../components/ui/empty";
import { Skeleton } from "../components/ui/skeleton";
import { appRoute } from "../router";

interface IsolatedAppFrameProps {
  appInstanceId: string;
  intendedPath: string;
  organizationId: string;
  origin: string;
  title: string;
  deploymentKey: string | null;
}

function IsolatedAppFrame({
  appInstanceId,
  intendedPath,
  organizationId,
  origin,
  title,
  deploymentKey,
}: IsolatedAppFrameProps) {
  const { handleLoad, launchError, launchKey, listenerReady, registerFrame, registerHost, retry } =
    useFrameLifecycle({ appInstanceId, intendedPath, organizationId, origin });

  let content;
  if (launchError) {
    content = (
      <div className="grid min-h-0 flex-1 place-items-center overflow-y-auto p-5">
        <Empty className="max-w-lg" role="alert">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <LockKeyholeIcon aria-hidden="true" />
            </EmptyMedia>
            <EmptyTitle>We couldn’t open this tool.</EmptyTitle>
            <EmptyDescription>{launchError}</EmptyDescription>
          </EmptyHeader>
          <EmptyContent>
            <Button type="button" onClick={retry}>
              <RefreshCwIcon aria-hidden="true" data-icon="inline-start" />
              Try again
            </Button>
          </EmptyContent>
        </Empty>
      </div>
    );
  } else if (!listenerReady) {
    content = (
      <section
        aria-busy="true"
        className="grid min-h-0 flex-1 place-content-center justify-items-center gap-4 text-center"
      >
        <Skeleton className="h-1.5 w-28" />
        <p className="m-0 text-sm text-muted-foreground">Preparing the secure app connection…</p>
      </section>
    );
  } else {
    content = (
      <iframe
        key={`${deploymentKey ?? "unversioned"}:${launchKey}`}
        ref={registerFrame}
        className="block min-h-0 w-full flex-1 border-0 bg-card"
        src={`${origin}/__platform/bootstrap?shellOrigin=${encodeURIComponent(window.location.origin)}`}
        title={title}
        sandbox="allow-scripts allow-same-origin allow-forms allow-downloads"
        onLoad={handleLoad}
      />
    );
  }

  return (
    <div className="contents" ref={registerHost}>
      {content}
    </div>
  );
}

function AvailableAppFrame({
  appInstanceId,
  intendedPath,
  detail,
}: {
  appInstanceId: string;
  intendedPath: string;
  detail: AppDetailResponse;
}) {
  const { platformUpdateAvailable } = useDeploymentUpdates();
  const [loadedDeploymentHash, setLoadedDeploymentHash] = useState(detail.deploymentHash);

  const toolUpdateAvailable =
    !platformUpdateAvailable &&
    loadedDeploymentHash !== null &&
    detail.deploymentHash !== null &&
    detail.deploymentHash !== loadedDeploymentHash;

  const { app, organization } = detail;
  return (
    <AuthenticatedShell
      activeOrganizationId={organization.id}
      backToToolsOrganizationId={organization.id}
      fillViewport
    >
      {toolUpdateAvailable ? (
        <DeploymentUpdateNotice
          kind="tool"
          onRefresh={() => {
            setLoadedDeploymentHash(detail.deploymentHash);
          }}
        />
      ) : null}
      <main
        id="main-content"
        className="app-frame-canvas flex min-h-0 flex-1 overflow-hidden bg-muted"
      >
        <IsolatedAppFrame
          key={`${appInstanceId}:${app.technicalOrigin}:${intendedPath}`}
          appInstanceId={appInstanceId}
          deploymentKey={loadedDeploymentHash}
          intendedPath={intendedPath}
          organizationId={organization.id}
          origin={app.technicalOrigin}
          title={app.displayName}
        />
      </main>
    </AuthenticatedShell>
  );
}

export function AppFramePage() {
  const { appInstanceId } = appRoute.useParams();
  const { path } = appRoute.useSearch();
  const isOnline = useOnlineStatus();
  const detail = useQuery({
    ...appQuery(appInstanceId),
    enabled: isOnline,
  });

  if (!isOnline) {
    return (
      <AuthenticatedShell fillViewport>
        <main
          className="app-frame-canvas grid min-h-0 flex-1 place-items-center overflow-y-auto px-4 py-8"
          id="main-content"
        >
          <OfflineState
            action={
              <Link className={buttonVariants({ variant: "outline" })} to="/">
                Return to tools
              </Link>
            }
            description="CCBox tools need an internet connection. Reconnect to Wi-Fi or cellular data to open this tool; CCBox will continue automatically."
          />
        </main>
      </AuthenticatedShell>
    );
  }

  if (detail.isPending) {
    return (
      <AuthenticatedShell fillViewport>
        <main
          id="main-content"
          aria-busy="true"
          className="app-frame-canvas grid min-h-0 flex-1 place-content-center justify-items-center gap-4 overflow-y-auto text-center"
        >
          <Skeleton className="h-1.5 w-28" />
          <p className="m-0 text-sm text-muted-foreground">Preparing the isolated workspace…</p>
        </main>
      </AuthenticatedShell>
    );
  }

  if (detail.isError) {
    return (
      <AuthenticatedShell fillViewport>
        <main
          id="main-content"
          className="app-frame-canvas grid min-h-0 flex-1 place-items-center overflow-y-auto px-5 py-12"
        >
          <Empty className="max-w-lg">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <LockKeyholeIcon aria-hidden="true" />
              </EmptyMedia>
              <EmptyTitle>This tool isn’t available.</EmptyTitle>
              <EmptyDescription>
                It may have been removed or your access may have changed.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Link className={buttonVariants({ variant: "default" })} to="/">
                Return to tools
              </Link>
            </EmptyContent>
          </Empty>
        </main>
      </AuthenticatedShell>
    );
  }

  return (
    <AvailableAppFrame appInstanceId={appInstanceId} detail={detail.data} intendedPath={path} />
  );
}
